import { useSyncExternalStore } from "react";

// The loader in public/index.html covers the page while the expensive start-up
// work (React mount, below-the-fold chunk, fonts, hero images, background
// sprites) happens. Entrance animations wait for `ready`, so they play on a
// quiet main thread instead of competing with that work.

const MIN_SHOW = 500; // avoids a flash on fast connections
const MAX_WAIT = 6000; // never hold a visitor on a slow network longer than this
const FADE = 600; // keep in sync with the #loader transition in index.html

let ready = false;
const listeners = new Set();

const holds = [];
/** Keeps the loader up until `promise` settles (or MAX_WAIT passes). */
export const holdLoader = (promise) => {
	holds.push(Promise.resolve(promise).catch(() => {}));
};

export const isReady = () => ready;

const subscribe = (fn) => {
	listeners.add(fn);
	return () => listeners.delete(fn);
};
export const useReady = () => useSyncExternalStore(subscribe, isReady);

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const frame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

const heroImages = () =>
	Promise.all(
		[...document.querySelectorAll("#top img, header img")].map((img) => img.decode().catch(() => {}))
	);

/** Call once, right after the first render. */
export const boot = async () => {
	const started = performance.now();
	holdLoader(import("./pages/home-page/sections"));
	// Once the first render has committed and painted, the hero <img>s exist and
	// the first screen's fonts have been requested, so `fonts.ready` covers them.
	for (let i = 0; i < 10 && !document.getElementById("top"); i++) await frame();
	await frame();
	holdLoader(heroImages());
	holdLoader(document.fonts.ready);

	await Promise.race([Promise.all(holds), wait(MAX_WAIT)]);
	await wait(Math.max(0, MIN_SHOW - (performance.now() - started)));
	// Two frames so anything mounted by the work above is painted before the reveal.
	await frame();
	await frame();

	ready = true;
	listeners.forEach((fn) => fn());
	document.documentElement.classList.remove("is-loading");
	const loader = document.getElementById("loader");
	if (loader) setTimeout(() => loader.remove(), FADE + 100);
};
